export type GovernedStageOperation =
  | 'skip_ai_interview'
  | 'end_ai_interview_to_f2f'
  | 'rollback_f2f_to_ai_interview'
  | 'skip_f2f'
  | 'rollback_offer_to_f2f'
  | 'skip_offer'
  | 'rollback_contract_to_offer'
  | 'skip_contract'
  | 'disqualify';

export type LateStageActionId = GovernedStageOperation | 'schedule_f2f' | 'move_to_f2f' | 'move_to_contract';

export type LateStageAction = {
  id: LateStageActionId;
  blockedReason: string | null;
};

export type LateStageSummary = {
  hasAcceptedOffer: boolean;
  hasSignedContract: boolean;
  hasAiInterviewBasis: boolean;
  allowedOperations: string[];
  denialReasons: Record<string, string>;
};

const LATE_STAGE_PIPELINE_STAGES = new Set([
  'shortlisted',
  'shortlisting',
  'interviewed',
  'second_interview',
  'offering',
  'signing_contract',
  'hired',
]);

export function isLateStagePipelineStage(stage: string | null | undefined) {
  return !!stage && LATE_STAGE_PIPELINE_STAGES.has(stage);
}

export function resolveLateStageActions(input: {
  stage: string | null | undefined;
  pipelineAllowed: boolean;
  summary: LateStageSummary;
}): LateStageAction[] {
  const stage = input.stage ?? '';
  const commonBlock = !input.pipelineAllowed ? 'candidate_pipeline_unavailable' : null;
  const action = (id: LateStageActionId, stateBlock: string | null = null): LateStageAction => ({
    id,
    blockedReason: commonBlock
      ?? stateBlock
      ?? (input.summary.allowedOperations.includes(id)
        ? null
        : input.summary.denialReasons[id] ?? 'operation_not_permitted'),
  });
  const directAction = (id: LateStageActionId, stateBlock: string | null = null): LateStageAction => ({
    id,
    blockedReason: commonBlock ?? stateBlock,
  });

  if (stage === 'shortlisted' || stage === 'shortlisting') {
    return [
      action(input.summary.hasAiInterviewBasis ? 'skip_ai_interview' : 'move_to_f2f'),
      action('disqualify'),
    ];
  }

  if (stage === 'interviewed') {
    return [
      action('schedule_f2f'),
      action('disqualify'),
    ];
  }

  if (stage === 'second_interview') {
    const rollbackBlock = input.summary.hasSignedContract
      ? 'contract_already_signed'
      : input.summary.hasAcceptedOffer
        ? 'offer_already_accepted'
        : null;
    return [
      action('rollback_f2f_to_ai_interview', rollbackBlock),
      action('schedule_f2f'),
      action('skip_f2f'),
      action('disqualify'),
    ];
  }

  if (stage === 'offering') {
    const rollbackBlock = input.summary.hasSignedContract
      ? 'contract_already_signed'
      : input.summary.hasAcceptedOffer
        ? 'offer_already_accepted'
        : null;
    return [
      action('rollback_offer_to_f2f', rollbackBlock),
      input.summary.hasAcceptedOffer
        ? directAction('move_to_contract')
        : action('skip_offer'),
      action('disqualify'),
    ];
  }

  if (stage === 'signing_contract') {
    const rollbackBlock = input.summary.hasSignedContract
      ? 'contract_already_signed'
      : input.summary.hasAcceptedOffer
        ? 'offer_already_accepted'
        : null;
    return [
      action('rollback_contract_to_offer', rollbackBlock),
      action('skip_contract', input.summary.hasSignedContract ? 'contract_already_signed' : null),
      action('disqualify'),
    ];
  }

  if (stage === 'hired') {
    return [action('disqualify')];
  }

  return [];
}
